/// <reference types="@angular/localize" />

import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { DmBulletItem, DmBulletList, DmButton, DmInput } from '@digitale-menschen/component-library-angular';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { form, FormField, FormRoot } from '@angular/forms/signals';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { getLexplusResetPasswordDtoInitialValue, lexplusResetPasswordDtoSchema } from '@lexplus-authentication-api';
import { authenticationControllerResetPasswordV1 } from '@lexplus-authentication-api';
import { lastValueFrom } from 'rxjs';
import { API_CONFIGURATION } from '@lexplus-environments';
import { Store } from '@ngxs/store';
import {
  fieldErrorMessage,
  FormAlertComponent,
  LOCALIZED_SHARED_ROUTES,
  LocalizedRouteService,
  notify,
  SHARED_ROUTE_IDS,
  TemporarySessionState,
} from '@lexplus-shared-lib';
import { TemporarySessionStateClear } from '@lexplus-shared-lib';
import { AuthenticationService } from '../../services/authentication.service';
import { AUTH_ROUTE_IDS, LOCALIZED_AUTH_ROUTES } from '../../auth-route-const';

@Component({
  selector: 'lp-password-reset-form',
  imports: [DmButton, DmInput, RouterLink, FormField, FormRoot, DmBulletList, DmBulletItem, FormAlertComponent],
  templateUrl: './password-reset-form.component.html',
})
export class LexplusPasswordResetFormComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly localizedRouteService = inject(LocalizedRouteService);

  public readonly initialPasswordMode = input<boolean>(false);
  public readonly isForAdmin = input<boolean>(false);

  private readonly resetPasswordModel = signal(getLexplusResetPasswordDtoInitialValue());
  protected readonly resetPasswordForm = form(this.resetPasswordModel, lexplusResetPasswordDtoSchema, {
    submission: { action: () => this.resetPassword() },
  });

  protected readonly isForced = signal<boolean>(false);
  protected readonly loadingStatus = signal<'loading' | 'success' | 'error' | null>(null);
  /** Reset failure, shown inside the form instead of as a toast. */
  protected readonly formErrorMessage = signal<string | null>(null);
  protected readonly confirmPassword = signal('');

  private readonly password = computed<string>(() => this.resetPasswordForm.password().value());

  protected readonly passwordErrorMessage = computed(() =>
    fieldErrorMessage(this.resetPasswordForm.password(), $localize`:@@auth.passwordReset.passwordRequired:Passwort ist erforderlich.`),
  );

  protected readonly passwordPolicy = computed(() => this.authenticationService.checkPasswordPolicy(this.password()));

  /** Whether the password matches its confirmation (and is not empty). */
  private readonly passwordsMatch = computed<boolean>(() => this.password().length > 0 && this.password() === this.confirmPassword());

  /** The confirmation is not form-bound, so its one rule is checked here rather than by the schema. */
  protected readonly confirmPasswordErrorMessage = computed(() =>
    this.resetPasswordForm.password().touched() && !this.passwordsMatch()
      ? $localize`:@@auth.passwordReset.passwordsMismatch:Die Passwörter stimmen nicht überein`
      : '',
  );

  /** Whether the form may be submitted: the full policy is satisfied and the passwords match. */
  protected readonly isFormValid = computed<boolean>(() => this.passwordPolicy().isValid && this.passwordsMatch());

  /** Editing the password retracts the previous failure, so the alert never contradicts the input. */
  private readonly clearErrorOnPasswordEditEffect = effect(() => {
    this.password();
    this.formErrorMessage.set(null);
  });

  protected loginRoute = '';

  public ngOnInit(): void {
    this.loginRoute =
      '/' +
      this.localizedRouteService.getLocalizedRoute(LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT], LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGIN]);

    // Get token and email from URL query parameters
    const token = this.activatedRoute.snapshot.queryParams['token'];
    const email = this.activatedRoute.snapshot.queryParams['email'];
    const changetype = this.activatedRoute.snapshot.queryParams['changetype'] || 'self';
    this.isForced.set(changetype === 'forced');

    if (!token || !email) {
      notify.error($localize`:@@auth.passwordReset.invalid:Ungültiger Link`, {
        description: $localize`:@@auth.passwordReset.invalidDescription:Der Link zum Zurücksetzen ist ungültig oder abgelaufen.`,
      });
      void this.router.navigateByUrl(this.loginRoute);
      return;
    }

    // Set token and email in the form
    this.resetPasswordForm.token().value.set(token);
    this.resetPasswordForm.email().value.set(email);
    this.resetPasswordForm.changetype().value.set(changetype);
  }

  /**
   * Handles the password reset workflow by sending the new password
   * and verification token to the authentication service.
   */
  private async resetPassword(): Promise<void> {
    this.loadingStatus.set('loading');
    // Cleared up front so a repeated failure re-mounts the alert, which both
    // re-announces it and makes the retry visible.
    this.formErrorMessage.set(null);

    try {
      await lastValueFrom(
        authenticationControllerResetPasswordV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
          body: this.resetPasswordForm().value(),
        }),
      );

      this.loadingStatus.set('success');
      notify.success($localize`:@@auth.passwordReset.success:Passwort zurückgesetzt`, {
        description: $localize`:@@auth.passwordReset.successDescription:Das Passwort wurde geändert. Die Anmeldung ist jetzt mit dem neuen Passwort möglich.`,
      });

      const initialPasswordOrMustChangePassword =
        this.initialPasswordMode() || this.store.selectSnapshot(TemporarySessionState.mustChangePassword);

      // The login flow is finished → clear the short-lived session state (token included).
      this.store.dispatch(new TemporarySessionStateClear());

      if (initialPasswordOrMustChangePassword) {
        await this.authenticationService.loadProfileAndSetInState();
        const dashboardRoute = '/' + this.localizedRouteService.getLocalizedRoute(LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.DASHBOARD]);
        await this.router.navigateByUrl(dashboardRoute);
        return;
      }

      // Navigate to login after success
      await this.router.navigateByUrl(this.loginRoute);
    } catch (error) {
      this.loadingStatus.set('error');
      // Show the server's error message (e.g. password policy/blacklist), else fall back.
      const serverMessage = (error as HttpErrorResponse)?.error?.message;
      this.formErrorMessage.set(
        serverMessage ?? $localize`:@@auth.passwordReset.failedDescription:Bitte erneut versuchen. Der Link ist möglicherweise abgelaufen.`,
      );
    }
  }
}
